import React, { useState, useEffect, useCallback } from 'react';
import { analysisApi } from '../api';

interface PersonalityRecord {
  id: number;
  person_id: number;
  traits: Record<string, string>;
  summary: string;
  analysis: string;
  generated_at: string;
  updated_at: string;
}

interface PersonalityDisplayProps {
  personId: number;
  personName: string;
}

const PersonalityDisplay: React.FC<PersonalityDisplayProps> = ({ personId, personName }) => {
  const [record, setRecord] = useState<PersonalityRecord | null>(null);
  const [loading, setLoading] = useState(true);
  const [analyzing, setAnalyzing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showFullAnalysis, setShowFullAnalysis] = useState(false);

  const loadPersonality = useCallback(async () => {
    try {
      setLoading(true);
      const response = await analysisApi.getByPersonId(personId);
      setRecord(response.data);
      setError(null);
    } catch (err: any) {
      if (err.response?.status === 404) {
        setError('暂无性格记录');
      } else {
        setError('加载性格记录失败: ' + (err.response?.data?.detail || err.message));
      }
    } finally {
      setLoading(false);
    }
  }, [personId]);

  useEffect(() => {
    loadPersonality();
  }, [loadPersonality]);

  const handleAnalyze = async () => {
    setAnalyzing(true);
    try {
      await analysisApi.analyze(personId);
      await loadPersonality();
    } catch (error: any) {
      alert('分析失败: ' + (error.response?.data?.detail || error.message));
    } finally {
      setAnalyzing(false);
    }
  };

  if (loading) {
    return (
      <div className="ply-loading">
        <div className="ply-loading-spinner" />
        <p>加载性格数据...</p>
        <style>{`
          .ply-loading {
            display: flex; flex-direction: column; align-items: center;
            padding: 48px 20px; gap: 12px;
          }
          .ply-loading-spinner {
            width: 32px; height: 32px;
            border: 3px solid var(--border); border-top-color: var(--primary);
            border-radius: 50%; animation: plySpin 0.8s linear infinite;
          }
          @keyframes plySpin { to { transform: rotate(360deg); } }
          .ply-loading p { color: var(--text-tertiary); font-size: 13px; }
        `}</style>
      </div>
    );
  }

  if (error && !record) {
    return (
      <div className="ply-empty">
        <div className="ply-empty-icon">🧠</div>
        <h3 className="ply-empty-title">暂无性格分析</h3>
        <p className="ply-empty-desc">
          添加一些日记记录后，AI 将为你分析 {personName} 的性格特征
        </p>
        <button className="ply-analyze-btn" onClick={handleAnalyze} disabled={analyzing}>
          {analyzing ? (
            <>
              <span className="ply-btn-spinner" />
              正在分析...
            </>
          ) : (
            <>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 1 1 7.072 0l-.548.547A3.374 3.374 0 0 0 14 18.469V19a2 2 0 1 1-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z"/>
              </svg>
              生成 AI 分析
            </>
          )}
        </button>
        <style>{`
          .ply-empty {
            display: flex; flex-direction: column; align-items: center;
            padding: 48px 24px; text-align: center;
          }
          .ply-empty-icon { font-size: 48px; margin-bottom: 12px; opacity: 0.8; }
          .ply-empty-title { font-size: 17px; font-weight: 600; color: var(--text-primary); margin: 0 0 8px; }
          .ply-empty-desc { font-size: 13px; color: var(--text-tertiary); margin: 0 0 24px; max-width: 260px; line-height: 1.6; }
          .ply-analyze-btn {
            display: flex; align-items: center; gap: 8px;
            padding: 12px 24px; border: none; border-radius: var(--radius-xl);
            background: var(--primary); color: white;
            font-size: 14px; font-weight: 600; font-family: inherit;
            cursor: pointer; box-shadow: var(--shadow-primary);
            transition: all var(--duration-normal) var(--ease-out);
          }
          .ply-analyze-btn:active { transform: scale(0.96); }
          .ply-analyze-btn:disabled { opacity: 0.7; cursor: not-allowed; }
          .ply-btn-spinner {
            width: 16px; height: 16px;
            border: 2px solid rgba(255,255,255,0.3); border-top-color: white;
            border-radius: 50%; animation: plySpin 0.6s linear infinite;
          }
          @keyframes plySpin { to { transform: rotate(360deg); } }
        `}</style>
      </div>
    );
  }

  if (!record) return null;

  const traitEntries = record.traits ? Object.entries(record.traits) : [];

  return (
    <div className="ply-container">
      {/* 头部操作栏 */}
      <div className="ply-header">
        <div className="ply-header-left">
          <h3 className="ply-section-title">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 1 1 7.072 0l-.548.547A3.374 3.374 0 0 0 14 18.469V19a2 2 0 1 1-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z"/>
            </svg>
            性格分析
          </h3>
          <span className="ply-update-time">
            {new Date(record.updated_at).toLocaleDateString('zh-CN')} 更新
          </span>
        </div>
        <button className="ply-refresh-btn" onClick={handleAnalyze} disabled={analyzing}>
          {analyzing ? (
            <span className="ply-refresh-spinner" />
          ) : (
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M21.5 2v6h-6M2.5 22v-6h6M2 11.5a10 10 0 0 1 18.8-4.3M22 12.5a10 10 0 0 1-18.8 4.2"/>
            </svg>
          )}
          重新分析
        </button>
      </div>

      {/* 性格特征卡片 */}
      {traitEntries.length > 0 && (
        <div className="ply-traits-card">
          <h4 className="ply-card-title">{personName} 的性格特征</h4>
          <div className="ply-traits-grid">
            {traitEntries.map(([key, value]) => (
              <div key={key} className="ply-trait-item">
                <span className="ply-trait-key">{key}</span>
                <span className="ply-trait-value">{value}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* 总结 */}
      {record.summary && (
        <div className="ply-summary-card">
          <div className="ply-summary-icon">💡</div>
          <div className="ply-summary-content">
            <h4 className="ply-card-title" style={{ marginBottom: '8px' }}>总结</h4>
            <p className="ply-summary-text">{record.summary}</p>
          </div>
        </div>
      )}

      {/* 详细分析 */}
      {record.analysis && (
        <div className="ply-analysis-card">
          <div className="ply-analysis-header" onClick={() => setShowFullAnalysis(!showFullAnalysis)}>
            <h4 className="ply-card-title">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><path d="M14 2v6h6"/><path d="M16 13H8"/><path d="M16 17H8"/>
              </svg>
              详细分析
            </h4>
            <svg 
              width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
              style={{ transform: showFullAnalysis ? 'rotate(180deg)' : 'rotate(0)', transition: 'transform 200ms' }}
            >
              <path d="m6 9 6 6 6-6"/>
            </svg>
          </div>
          <div className={`ply-analysis-body ${showFullAnalysis ? 'expanded' : ''}`}>
            <div className="ply-analysis-text">
              {record.analysis}
            </div>
          </div>
        </div>
      )}

      <style>{`
        .ply-container {
          animation: plyFadeIn 0.3s var(--ease-out);
        }

        @keyframes plyFadeIn {
          from { opacity: 0; transform: translateY(6px); }
          to { opacity: 1; transform: translateY(0); }
        }

        /* Header */
        .ply-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 16px;
        }

        .ply-header-left {
          display: flex;
          align-items: center;
          gap: 12px;
        }

        .ply-section-title {
          display: flex;
          align-items: center;
          gap: 8px;
          margin: 0;
          font-size: 16px;
          font-weight: 600;
          color: var(--text-primary);
        }

        .ply-update-time {
          font-size: 12px;
          color: var(--text-tertiary);
          font-weight: 500;
        }

        .ply-refresh-btn {
          display: flex;
          align-items: center;
          gap: 6px;
          padding: 8px 16px;
          border: 1.5px solid var(--border);
          border-radius: var(--radius-full);
          background: var(--surface);
          font-size: 13px;
          font-weight: 500;
          font-family: inherit;
          color: var(--text-secondary);
          cursor: pointer;
          transition: all var(--duration-fast);
        }

        .ply-refresh-btn:active {
          transform: scale(0.95);
        }

        .ply-refresh-btn:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }

        .ply-refresh-spinner {
          width: 14px;
          height: 14px;
          border: 2px solid var(--border);
          border-top-color: var(--primary);
          border-radius: 50%;
          animation: plySpin 0.6s linear infinite;
        }

        /* Traits Card */
        .ply-traits-card {
          background: linear-gradient(135deg, var(--primary) 0%, #A29BFE 100%);
          border-radius: var(--radius-xl);
          padding: 20px;
          margin-bottom: 12px;
          color: white;
        }

        .ply-traits-card .ply-card-title {
          color: rgba(255,255,255,0.9);
          margin: 0 0 14px;
          font-size: 14px;
          font-weight: 600;
        }

        .ply-traits-grid {
          display: flex;
          flex-wrap: wrap;
          gap: 8px;
        }

        .ply-trait-item {
          display: flex;
          align-items: center;
          gap: 6px;
          padding: 6px 14px;
          background: rgba(255,255,255,0.18);
          backdrop-filter: blur(4px);
          border-radius: var(--radius-full);
          font-size: 13px;
        }

        .ply-trait-key {
          font-weight: 600;
          opacity: 0.9;
        }

        .ply-trait-value {
          opacity: 0.85;
        }

        /* Summary Card */
        .ply-summary-card {
          display: flex;
          gap: 14px;
          background: var(--surface);
          border: 1px solid var(--border-light);
          border-radius: var(--radius-xl);
          padding: 18px 20px;
          margin-bottom: 12px;
          box-shadow: var(--shadow-sm);
        }

        .ply-summary-icon {
          font-size: 24px;
          flex-shrink: 0;
          margin-top: 2px;
        }

        .ply-summary-content {
          flex: 1;
          min-width: 0;
        }

        .ply-card-title {
          display: flex;
          align-items: center;
          gap: 8px;
          margin: 0;
          font-size: 14px;
          font-weight: 600;
          color: var(--text-primary);
        }

        .ply-summary-text {
          margin: 0;
          font-size: 14px;
          color: var(--text-secondary);
          line-height: 1.7;
        }

        /* Analysis Card */
        .ply-analysis-card {
          background: var(--surface);
          border: 1px solid var(--border-light);
          border-radius: var(--radius-xl);
          overflow: hidden;
          box-shadow: var(--shadow-sm);
          margin-bottom: 12px;
        }

        .ply-analysis-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 16px 20px;
          cursor: pointer;
          transition: background var(--duration-fast);
          color: var(--text-tertiary);
        }

        .ply-analysis-header:hover {
          background: var(--surface-hover);
        }

        .ply-analysis-body {
          max-height: 0;
          overflow: hidden;
          transition: max-height 300ms var(--ease-out);
        }

        .ply-analysis-body.expanded {
          max-height: 2000px;
        }

        .ply-analysis-text {
          padding: 0 20px 20px;
          font-size: 14px;
          color: var(--text-secondary);
          line-height: 1.8;
          white-space: pre-wrap;
          word-wrap: break-word;
        }
      `}</style>
    </div>
  );
};

export default PersonalityDisplay;
